import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import * as ReduxForm from 'redux-form';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import { INLINE } from '../../../constants/constant_display_type';
import FormattedCurrency from '../../../components/common/form/FormattedCurrency';
import FormattedPercentage from '../../../components/common/form/FormattedPercentage';
import * as Dropdowns from '../../../components/admin/dropdowns';
import FieldLabel from './FieldLabel';
import { getParticularPreferenceSettings } from '../../../common/selectors/preference';

class Field extends Component {
  constructor(props) {
    super(props);

    this.renderBooleanText = this.renderBooleanText.bind(this);
    this.renderCollectionText = this.renderCollectionText.bind(this);
    this.renderCurrencyText = this.renderCurrencyText.bind(this);
    this.renderDateText = this.renderDateText.bind(this);
    this.renderDateTimeText = this.renderDateTimeText.bind(this);
    this.renderInlineField = this.renderInlineField.bind(this);
    this.renderNumberText = this.renderNumberText.bind(this);
    this.renderObjectText = this.renderObjectText.bind(this);
    this.renderPercentageText = this.renderPercentageText.bind(this);
    this.renderStaticText = this.renderStaticText.bind(this);
    this.renderText = this.renderText.bind(this);
    this.renderTimeText = this.renderTimeText.bind(this);
  }

  renderBooleanText(field) {
    const { customDisplay = {} } = this.props;

    if (!isEmpty(customDisplay)) {
      const customTrue = customDisplay.customTrue || {};
      const customFalse = customDisplay.customFalse || {};
      const customDefault = customDisplay.customDefault || {};

      if (customTrue.value === field) {
        return customTrue.text;
      }
      if (customFalse.value === field) {
        return customFalse.text;
      }
      if (customDefault.value === field) {
        return customDefault.text;
      }
    }

    return field ? 'Yes' : 'No';
  }

  renderNumberText(field) {
    const { customDisplay = {} } = this.props;

    if (!isEmpty(customDisplay)) {
      const { customNumber = {} } = customDisplay;

      if (customNumber.value === field) {
        return customNumber.text;
      }
    }

    return Number(field) || '';
  }

  renderCurrencyText(field) {
    const { currencyCode, amount, currencyDisplay, currencySymbol } =
      this.props;

    let value = field;
    if (amount) value = amount;
    return (
      <FormattedCurrency
        value={Number(value || 0)}
        currency={currencyCode}
        symbol={currencySymbol}
        currencyDisplay={currencyDisplay}
      />
    );
  }

  renderPercentageText(field) {
    const { currencyCode, amount } = this.props;
    let value = field;
    if (amount) value = amount;
    return (
      <FormattedPercentage value={Number(value || 0)} currency={currencyCode} />
    );
  }

  renderObjectText(field) {
    const {
      customDisplay = {},
      field_id,
      fieldId,
      fieldLabel,
      list,
      listOptions,
    } = this.props;

    if (!isEmpty(customDisplay)) {
      const { customObject = {}, textFn } = customDisplay;
      const { value, text, useLink, linkPath } = customObject;

      if (useLink && field_id && typeof field === 'string') {
        const path = linkPath.replace(/:_id/g, field_id);
        return <a href={path} target="_blank" rel="noopener noreferrer">{field}</a>;
      }

      if (value === field) {
        return text;
      }

      if (typeof textFn === 'function') {
        return textFn(field);
      }
    }

    if (typeof field === 'object') {
      return (field || {}).name || null;
    }

    if (list || listOptions) {
      const selectedListItem =
        (list || listOptions || []).find((listItem) => {
          return listItem[fieldId] === field;
        }) || {};

      return selectedListItem['name'] || selectedListItem[fieldLabel];
    }

    return field;
  }

  renderCollectionText(field) {
    const { fieldNameKey = 'name', customDisplay = {} } = this.props;
    const fields = field || [];

    if (!isEmpty(customDisplay)) {
      const { customCollection = {} } = customDisplay;
      const { useLink, linkPath } = customCollection;

      if (useLink) {
        return fields.map((field) => {
          const path = linkPath.replace(/:_id/g, field._id);
          const label = field[fieldNameKey];
          if (field._id) {
            return (
              <div>
                <a href={path} target="_blank" rel="noopener noreferrer">{field[fieldNameKey]}</a>
              </div>
            );
          } else {
            return label;
          }
        });
      }
    }

    const names = fields.map((field) => {
      const flatField = flatten(field);
      return flatField[fieldNameKey];
    });

    const strNames = names.toString();
    const text = strNames.replace(/,/g, ', ');
    return text;
  }

  renderDateText(field) {
    if (!field) return null;

    const { dateFormat } = this.props;

    return moment(field).format(dateFormat);
  }

  renderTimeText(field) {
    if (!field) return null;

    const { timeFormat } = this.props;

    return moment(field).format(timeFormat);
  }

  renderDateTimeText(field) {
    if (!field) return null;

    const { dateTimeFormat } = this.props;

    return moment(field).format(dateTimeFormat);
  }

  renderStringText(field) {
    const { customDisplay } = this.props;

    if (!isEmpty(customDisplay)) {
      const { customString = {} } = customDisplay;

      if (customString.value === field) {
        return customString.text;
      }
    }

    return field || '';
  }

  renderStaticText() {
    const { staticValue, fieldType, label = '', placeholder = '' } = this.props;
    let text = staticValue;

    if (fieldType === 'boolean') text = this.renderBooleanText(text);
    if (fieldType === 'number') text = this.renderNumberText(text);
    if (fieldType === 'currency') text = this.renderCurrencyText(text);
    if (fieldType === 'object') text = this.renderObjectText(text);
    if (fieldType === 'collection') text = this.renderCollectionText(text);
    if (fieldType === 'date') text = this.renderDateText(text);
    if (fieldType === 'time') text = this.renderTimeText(text);
    if (fieldType === 'datetime') text = this.renderDateTimeText(text);
    if (fieldType === 'percent') text = this.renderPercentageText(text);
    if (fieldType === 'string') text = this.renderStringText(text);

    if (!text && (label || placeholder)) {
      text = `No ${(label || placeholder).toLowerCase() || 'value'}`;
    }
    return text;
  }

  renderTagText() {
    if (this.props.field) {
      return this.props.field.join(', ');
    }
    return '';
  }

  renderText() {
    const {
      field,
      label = '',
      placeholder = '',
      isStatic = false,
      fieldType,
    } = this.props;

    if (isStatic) {
      // Added this isStatic field so we can supply our own values into this Field
      return this.renderStaticText();
    }

    let text = field;
    if (fieldType === 'boolean') text = this.renderBooleanText(field);
    if (fieldType === 'number') text = this.renderNumberText(field);
    if (fieldType === 'currency') text = this.renderCurrencyText(field);
    if (fieldType === 'object') text = this.renderObjectText(field);
    if (fieldType === 'collection') text = this.renderCollectionText(field);
    if (fieldType === 'date') text = this.renderDateText(field);
    if (fieldType === 'time') text = this.renderTimeText(field);
    if (fieldType === 'datetime') text = this.renderDateTimeText(field);
    if (fieldType === 'percent') text = this.renderPercentageText(field);
    if (fieldType === 'string') text = this.renderStringText(field);
    if (fieldType === 'tag') text = this.renderTagText(field);

    if (!text && (label || placeholder))
      text = `No ${(label || placeholder).toLowerCase()}`;
    return text;
  }

  renderInlineField() {
    let {
      displayType,
      element = 'div',
      field,
      hideEmptyInline,
      href,
      intl,
      label,
      name,
      textStyle,
      translation,
      txtFn,
      field_id,
    } = this.props;

    if (hideEmptyInline && !field) return null;

    const childElements = [];

    if (intl && translation) {
      let labelObj = translation.label;
      if (labelObj && labelObj.message) {
        label = intl.formatMessage(labelObj.message, labelObj.params);
      }
    }

    if (label) {
      const viewMode = displayType === INLINE;
      childElements.push(
        <FieldLabel key={label} viewMode={viewMode}>
          {label}
        </FieldLabel>
      );
    }

    let textElement = null;

    if (typeof txtFn === 'function') {
      textElement = txtFn(field, label);
    } else {
      const text = this.renderText();
      const _href = field_id && href ? href.replace(/:id/g, field_id) : href;
      const textElChild = _href
        ? <a href={_href} target="_blank" rel="noopener noreferrer">{text}</a>
        : text;

      textElement = React.createElement(
        element,
        {
          style: { marginBottom: '1em', ...textStyle },
          key: name,
        },
        textElChild
      );
    }

    childElements.push(textElement);
    return React.createElement(element, { className: 'field' }, childElements);
  }

  render() {
    const {
      component,
      displayType,
      fieldDropdownType,
      name,
      skipCustomField,
    } = this.props;

    if (displayType === INLINE && !skipCustomField) {
      return this.renderInlineField();
    }

    if (fieldDropdownType) {
      return React.createElement(Dropdowns[fieldDropdownType], this.props);
    }

    return (
      <ReduxForm.Field
        {...this.props}
        name={name}
        component={component}
        customDisplay={undefined}
      />
    );
  }
}

function mapStateToProps(state, ownProps) {
  const { form, fieldName, fieldId, fieldType, fieldNameFn } = ownProps;

  let name = ownProps.name;
  if (fieldName && (fieldType === 'object' || fieldType === 'collection')) {
    name = fieldName;
  }

  let field;
  let field_id;

  if (form) {
    const selector = ReduxForm.formValueSelector(form);
    if (fieldNameFn) {
      const fieldValue = selector(state, fieldName);
      field = fieldNameFn(fieldValue);
    } else {
      field = selector(state, name);
    }
    field_id = selector(state, fieldId);
  }

  const dateTimeFormat = (
    getParticularPreferenceSettings(state, 'date_time_format', ['value']) || {}
  ).value;

  const dateFormat = (
    getParticularPreferenceSettings(state, 'date_format', ['value']) || {}
  ).value;

  const timeFormat = (
    getParticularPreferenceSettings(state, 'time_format', ['value']) || {}
  ).value;

  return {
    field,
    field_id,
    dateTimeFormat,
    dateFormat,
    timeFormat,
  };
}

function flatten(data) {
  let result = {};
  function recurse(cur, prop) {
    if (Object(cur) !== cur) {
      result[prop] = cur;
    } else if (Array.isArray(cur)) {
      const l = cur.length;
      for (let i = 0; i < l; i++) recurse(cur[i], prop + '[' + i + ']');
      if (l === 0) result[prop] = [];
    } else {
      let isEmpty = true;
      for (let p in cur) {
        isEmpty = false;
        recurse(cur[p], prop ? prop + '.' + p : p);
      }
      if (isEmpty && prop) result[prop] = {};
    }
  }
  recurse(data, '');
  return result;
}

export default injectIntl(connect(mapStateToProps)(Field));
